import { useMemo } from "react";

import { USD_DECIMALS } from "config/factors";
import { shouldInvertChartPrice } from "domain/prices/chartInversion";
import { bigintToNumber } from "lib/numbers";
import { getTokensMap, getV2Tokens } from "sdk/configs/tokens";

import { TokensData } from "./types";
import { useOnchainTokenConfigs } from "./useOnchainTokenConfigs";
import { useTokenBalances } from "./useTokenBalances";
import { useTokenRecentPricesRequest } from "./useTokenRecentPricesData";

type TokensDataResult = {
  tokensData?: TokensData;
  pricesUpdatedAt?: number;
  isBalancesLoaded?: boolean;
  error?: Error;
};

const AUTO_VISUAL_THRESHOLD = 0.01;
const AUTO_VISUAL_STEP = 1000;
const AUTO_VISUAL_MAX = 1_000_000_000_000;
function getAutoVisualConfig(tokenConfig, prices) {
  if (!prices?.minPrice || tokenConfig?.visualMultiplier || tokenConfig?.visualPrefix || tokenConfig?.isStable) {
    return undefined;
  }

  const priceNumber = bigintToNumber(prices.minPrice, USD_DECIMALS);
  if (!Number.isFinite(priceNumber) || priceNumber <= 0 || priceNumber >= AUTO_VISUAL_THRESHOLD) {
    return undefined;
  }

  if (shouldInvertChartPrice(prices.minPrice)) {
    return undefined;
  }

  let multiplier = AUTO_VISUAL_STEP;
  while (priceNumber * multiplier < 1 && multiplier < AUTO_VISUAL_MAX) {
    multiplier *= AUTO_VISUAL_STEP;
  }

  return { visualMultiplier: multiplier, visualPrefix: "" };
}

export function useTokensDataRequest(chainId: number): TokensDataResult {
  const tokenConfigs = getTokensMap(chainId);
  const { balancesData } = useTokenBalances(chainId);
  const { pricesData, updatedAt: pricesUpdatedAt, error: pricesError } = useTokenRecentPricesRequest(chainId);
  const { data: onchainConfigsData } = useOnchainTokenConfigs(chainId);

  // Prices are required — if they fail, we can't show any meaningful market data.
  // Balances and onchain token configs are optional — markets can still render without them.
  const error = pricesError;

  return useMemo(() => {
    if (error) {
      return {
        error,
      };
    }

    const tokenAddresses = getV2Tokens(chainId).map((token) => token.address);

    if (!pricesData) {
      return {
        tokensData: undefined,
        pricesUpdatedAt: undefined,
      };
    }

    const isBalancesLoaded = Boolean(balancesData);

    return {
      tokensData: tokenAddresses.reduce((acc: TokensData, tokenAddress) => {
        const prices = pricesData[tokenAddress];
        const balance = balancesData?.[tokenAddress];
        const tokenConfig = tokenConfigs[tokenAddress];
        const onchainConfig = onchainConfigsData?.[tokenAddress];

        if (!prices) {
          return acc;
        }

        const mergedConfig = {
          ...tokenConfig,
          ...onchainConfig,
        };
        const autoVisualConfig = getAutoVisualConfig(mergedConfig, prices);

        acc[tokenAddress] = {
          ...mergedConfig,
          ...(autoVisualConfig || {}),
          prices,
          balance,
        };

        return acc;
      }, {} as TokensData),
      pricesUpdatedAt,
      isBalancesLoaded,
    };
  }, [error, chainId, pricesData, pricesUpdatedAt, balancesData, tokenConfigs, onchainConfigsData]);
}
